import { Component, OnInit, ViewChild, AfterViewInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, UserService, UtilsService } from 'src/app/services';
import { DocumentType, DocumentTypeSerialized } from 'src/app/models/document_type.model';
import { User } from 'src/app/models/user.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { CreateDocumentTypeDialogComponent } from 'src/app/components/create-document-type-dialog/create-document-type-dialog.component';
import { DocumentRelatedType, DocumentRelatedTypeLabels } from 'src/app/enums/document-related-type.enum';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';

@Component({
  selector: 'app-document-types-list',
  templateUrl: './document-types-list.component.html',
  styleUrls: ['./document-types-list.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatChipsModule,
  ],
})
export class DocumentTypesListComponent extends IndexTemplateComponent implements OnInit, AfterViewInit {
  private userService = inject(UserService);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  protected me: User;
  dataSource = new MatTableDataSource<DocumentTypeSerialized>([]);

  displayedColumns: string[] = ['name', 'related_type', 'related_user_can_view_document', 'documents_count', 'actions'];

  override filterFormExtraParams: any = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.me = this.userService.getCurrentUser();

    this.processUrlParams();

    const urlPage = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (!urlPage) {
      this.pageIndex = 0;
    }

    super.ngOnInit();
  }

  private processUrlParams(): void {
    const urlPerPage = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (urlPerPage) {
      this.pageSize = parseInt(urlPerPage, 10);
    }

    const urlPage = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (urlPage) {
      this.pageIndex = parseInt(urlPage, 10) - 1;
    }
  }

  ngAfterViewInit(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm: string): void => {
        if (searchTerm !== '') {
          this.pageIndex = 0;
          this.fetchData();
        }
      });
  }

  override fetchData(): void {
    const searchTerm = this.filterForm.get('search')?.value || '';

    const params: { page: number; per_page: number; search?: string } = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    this.api.getDocumentTypes(params).subscribe({
      next: (response: ArrayResponse<DocumentType>): void => {
        this.dataSource.data = this.serializeDocumentTypes(response.data);
        this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
      },
    });
  }

  createDocumentType(): void {
    const dialogRef = this.dialog.open(CreateDocumentTypeDialogComponent, {
      width: '500px',
      data: { mode: 'create' },
    });

    dialogRef.afterClosed().subscribe((result: { success?: boolean } | undefined): void => {
      if (!result?.success) return;

      this.pageIndex = 0;
      this.fetchData();
    });
  }

  deleteDocumentType(documentType: DocumentType): void {
    if (!confirm(`¿Estás seguro de que quieres eliminar el tipo de documento "${documentType.name}"?`)) {
      return;
    }

    this.api.deleteDocumentType(documentType.id).subscribe({
      next: () => {
        const currentPageItems = this.dataSource.data.length;
        if (currentPageItems === 1 && this.pageIndex > 0) {
          this.pageIndex = this.pageIndex - 1;
        }

        this.fetchData();
        this.snackBar.open('Tipo de documento eliminado correctamente', 'Cerrar', { duration: 3000 });
      },
    });
  }

  private serializeDocumentTypes(documentTypes: DocumentType[]): DocumentTypeSerialized[] {
    return documentTypes.map(docType => ({
      ...docType,
      relatedTypeLabel: this.getRelatedTypeLabel(docType.related_type),
      publicLabel: this.getPublicLabel(docType.related_user_can_view_document),
      documents_count: docType.documents_count || 0,
    }));
  }

  private getRelatedTypeLabel(relatedType: string | null): string {
    if (!relatedType) {
      return '-';
    }

    return DocumentRelatedTypeLabels[relatedType as DocumentRelatedType];
  }

  private getPublicLabel(isPublic: boolean): string {
    return isPublic ? 'RRHH + Usuario' : 'RRHH';
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
  }

  override changePage(event: PageEvent): void {
    this.onPageChange(event);
    super.changePage(event);
  }
}
